/**
 * New Q&A: (1) the questionnaire, the collections to answer from and the AI
 * model → "Extract questions"; (2) review the proposed list → "Answer". The
 * run is created only at step 2, over the same upload (extraction does not use
 * it up), and then runs in the background. The model chosen in step 1 —
 * a Foundry connection + deployment, or the pinned platform model — both
 * extracts the questions and writes the answers.
 */

import { useEffect, useState } from "react";
import { Alert, Button, Group, Paper, Stack, Stepper, Text, TextInput } from "@mantine/core";
import { IconListSearch, IconMessageQuestion } from "@tabler/icons-react";
import { useAccessToken } from "@/features/auth/useAccessToken";
import {
  ChatModelPicker,
  CollectionDocumentPicker,
  PLATFORM_CHOICE_ID,
  SolutionUpload,
  choiceToRequest,
  defaultChoiceWithPlatform,
  useChatModels,
  type ChatModelChoice,
  type CollectionSelection,
} from "@/features/solutions/shared";
import type { ChatConnectionOptionOut, PlatformChatModelOut } from "@/shared/api/ai";
import { ApiError } from "@/shared/api/apiFetch";
import { createRun, type UploadRefOut } from "@/shared/api/solutionRuns";
import { extractQuestions } from "@/shared/api/solutionsQa";
import { toastApiError } from "@/shared/ui/toast";
import { toRunQuestions, type DraftQuestion } from "./qaTypes";
import { QuestionEditor } from "./QuestionEditor";

interface Props {
  onCreated: (runId: string) => void;
}

/** "Connection · model" for the chosen model, as step 2 shows it. */
function choiceLabel(
  choice: ChatModelChoice,
  options: ChatConnectionOptionOut[] | null,
  platform: PlatformChatModelOut | null,
): string {
  if (choice.connectionId === PLATFORM_CHOICE_ID) return platform?.label ?? "Platform model";
  const conn = options?.find((o) => o.connection_id === choice.connectionId);
  const dep = conn?.deployments.find((d) => d.name === choice.deployment);
  return `${conn?.name ?? "AI connection"} · ${dep?.label ?? choice.deployment ?? ""}`;
}

function defaultName(upload: UploadRefOut | null): string {
  if (!upload) return "";
  return upload.filename
    .replace(/\.[^.]+$/, "")
    .replace(/[_-]+/g, " ")
    .trim();
}

export function QaWizard({ onCreated }: Props) {
  const getToken = useAccessToken();
  const [step, setStep] = useState(0);
  const [upload, setUpload] = useState<UploadRefOut | null>(null);
  const [collections, setCollections] = useState<CollectionSelection[]>([]);
  const [name, setName] = useState("");
  const [rows, setRows] = useState<DraftQuestion[]>([]);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState<"extract" | "answer" | null>(null);
  const models = useChatModels(true);
  const [modelChoice, setModelChoice] = useState<ChatModelChoice>({
    connectionId: null,
    deployment: null,
  });

  const modelOptions = models.options;
  const platformModel = models.platform;
  useEffect(() => {
    if (modelOptions) {
      setModelChoice((current) => defaultChoiceWithPlatform(modelOptions, platformModel, current));
    }
  }, [modelOptions, platformModel]);

  const confirmed = toRunQuestions(rows);
  const modelReady = Boolean(modelChoice.connectionId && modelChoice.deployment);

  const reset = () => {
    setStep(0);
    setUpload(null);
    setCollections([]);
    setName("");
    setRows([]);
    setNotice(null);
  };

  const extract = async () => {
    if (!upload) return;
    setBusy("extract");
    try {
      const result = await extractQuestions(
        await getToken(),
        upload.upload_id,
        choiceToRequest(modelChoice),
      );
      setRows(
        result.questions.map((q) => ({
          key: q.id,
          question: q.question,
          source_location: q.source_location ?? null,
          verbatim: q.verbatim,
          added: false,
        })),
      );
      setNotice(
        result.truncated
          ? `The document is long: ${result.skipped_units} parts at the end were not searched for questions. Add any that are missing.`
          : result.questions.length === 0
            ? "No questions were found. Add them below, or upload a different file."
            : null,
      );
      if (!name.trim()) setName(defaultName(upload));
      setStep(1);
    } catch (err) {
      toastApiError("Could not extract the questions", err);
    } finally {
      setBusy(null);
    }
  };

  const answer = async () => {
    if (!upload) return;
    setBusy("answer");
    try {
      const run = await createRun(await getToken(), {
        kind: "qa",
        name: name.trim() || defaultName(upload) || "Q&A",
        collections: collections.map((c) => ({
          index_id: c.indexId,
          document_ids: c.documentIds,
        })),
        upload_ids: [upload.upload_id],
        params: { questions: confirmed, ...choiceToRequest(modelChoice) },
      });
      reset();
      onCreated(run.run_id);
    } catch (err) {
      if (err instanceof ApiError && err.code === "solution_upload_not_found") {
        setNotice("This upload was already used by a run. Upload the questionnaire again.");
      }
      toastApiError("Could not start the answers", err);
    } finally {
      setBusy(null);
    }
  };

  return (
    <Paper withBorder radius="md" p="lg">
      <Stepper active={step} size="sm" allowNextStepsSelect={false} onStepClick={setStep}>
        <Stepper.Step label="Questionnaire" description="File and collections">
          <Stack gap="md" mt="md">
            <SolutionUpload
              label="Questionnaire (PDF, DOCX, XLSX, CSV or TXT)"
              value={upload}
              onChange={setUpload}
              disabled={busy !== null}
            />
            <CollectionDocumentPicker
              label="Answer from these collections"
              value={collections}
              onChange={setCollections}
            />
            <ChatModelPicker
              options={modelOptions}
              platform={platformModel}
              error={models.error}
              value={modelChoice}
              onChange={setModelChoice}
              disabled={busy !== null}
              footnote="This model reads the questions and writes the answers. Searching your collections always runs on the platform."
            />
            <Group justify="flex-end">
              <Button
                leftSection={<IconListSearch size={16} />}
                onClick={() => void extract()}
                disabled={!upload || collections.length === 0 || !modelReady}
                loading={busy === "extract"}
              >
                Extract questions
              </Button>
            </Group>
          </Stack>
        </Stepper.Step>
        <Stepper.Step label="Questions" description="Review before answering">
          <Stack gap="md" mt="md">
            <Text size="sm" c="dimmed">
              These are the questions found in {upload?.filename ?? "the document"}, word for word.
              Edit, reorder, delete or add questions — nothing is answered until you confirm.
            </Text>
            {notice && (
              <Alert color="yellow" variant="light">
                {notice}
              </Alert>
            )}
            <QuestionEditor rows={rows} onChange={setRows} disabled={busy !== null} />
            <Text size="sm" data-testid="qa-model">
              Answered by: {choiceLabel(modelChoice, modelOptions, platformModel)}
            </Text>
            <TextInput
              label="Name of this run"
              value={name}
              onChange={(e) => setName(e.currentTarget.value)}
              maxLength={200}
              disabled={busy !== null}
            />
            <Group justify="space-between">
              <Button variant="default" onClick={() => setStep(0)} disabled={busy !== null}>
                Back
              </Button>
              <Button
                leftSection={<IconMessageQuestion size={16} />}
                onClick={() => void answer()}
                disabled={confirmed.length === 0 || collections.length === 0 || !modelReady}
                loading={busy === "answer"}
              >
                Answer {confirmed.length} question{confirmed.length === 1 ? "" : "s"}
              </Button>
            </Group>
          </Stack>
        </Stepper.Step>
      </Stepper>
    </Paper>
  );
}
